import type { AppearanceColors } from 'src/generated/graphql-manager';

import type { ColorKeys, ColorSet } from './theme-and-color/theme-and-color';

import { useReactiveVar } from '@apollo/client';
import {
  Accordion,
  ImageIcon,
  InputDescription,
  SaveButton,
  TextInput,
  ThemeIcon,
  TypographyIcon,
} from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Theme, useGetLsproBrandingSettingsQuery, useUpdateThemeMutation } from 'src/generated/graphql-manager';
import { useCurrentUser } from 'src/manager/hooks/current-user';
import { isValidHexValue } from 'src/utils';
import { removeTypename } from 'src/utils/remove-typename';

import { FontAndTypography } from './font-and-typography';
import { LogoAndFavicon } from './logo-and-favicon';
import { ThemeAndColor } from './theme-and-color';
import { areColorSetsEqual } from './theme-and-color/color-set-utils';
import { type ThemePreviewState, themePreviewVar } from './theme-preview';

import styles from './branding.module.css';

const buildInitialColorErrorState = (appearanceColors?: AppearanceColors[]): ColorSet[] => {
  if (!appearanceColors) return [];

  return appearanceColors.map((obj) => {
    const { theme, ...colors } = obj;

    const errorState = Object.keys(colors).reduce((acc, key) => ({ ...acc, [key]: '' }), {
      theme,
      ...colors,
    });

    return errorState;
  });
};

export function ThemeSettings() {
  const { t } = useTranslation();
  const { lsproId } = useCurrentUser();
  const { data } = useGetLsproBrandingSettingsQuery({ variables: { lsproId } });
  const preview = useReactiveVar(themePreviewVar);

  const theme = data?.lsproSettings?.themes?.find((theme) => theme.id === preview?.themeId);

  const [name, setName] = useState(theme?.name || '');
  const [themeMode, setThemeMode] = useState(theme?.mode || Theme.DARK);
  const [headingFont, setHeadingFont] = useState(theme?.fonts.headingFont || '');
  const [textFont, setTextFont] = useState(theme?.fonts.textFont || '');
  // Falls back to `true` while the theme is still loading; AppearanceTheme.showLogoInPlayer defaults to true on the BE.
  const [showLogoInPlayer, setShowLogoInPlayer] = useState(theme?.showLogoInPlayer ?? true);
  const [assetUploading, setAssetUploading] = useState(false);

  const [appearanceColors, setAppearanceColors] = useState<ColorSet[]>(
    (theme?.appearanceColors || []).map(removeTypename)
  );

  const [colorErrors, setColorErrors] = useState<ColorSet[]>(buildInitialColorErrorState(theme?.appearanceColors));

  const hasErrors = colorErrors?.some(({ theme, ...errors }) => Object.values(errors).some(Boolean));

  const handleColorChange = (name: ColorKeys, value: string) => {
    setColorErrors((state) => state.map((color) => (color.theme === themeMode ? { ...color, [name]: '' } : color)));
    setAppearanceColors((state) =>
      state?.map((color) => (color.theme === themeMode ? { ...color, [name]: value } : color))
    );
  };

  const validateColorValue = (name: ColorKeys) => {
    setColorErrors((state) =>
      state.map((colorSet) => {
        if (colorSet.theme !== themeMode) return colorSet;

        const value = appearanceColors?.find((colorSet) => colorSet.theme === themeMode)?.[name] || '';

        return {
          ...colorSet,
          [name]: isValidHexValue(value) ? '' : t('manager.branding.themeAndColor.errors.invalidColorFormat'),
        };
      })
    );
  };

  const [updateThemeMutation, { loading }] = useUpdateThemeMutation();

  const handleSave = () => {
    if (!theme || hasErrors) return;

    // Build minimal input with only changed values
    const input: {
      lsproId: string;
      themeId: string;
      name?: string;
      mode?: Theme;
      fonts?: { headingFont: string; textFont: string };
      appearanceColors?: ColorSet[];
      showLogoInPlayer?: boolean;
    } = {
      lsproId,
      themeId: theme.id,
    };

    if (name !== theme.name) input.name = name;
    if (themeMode !== theme.mode) input.mode = themeMode;
    if (headingFont !== theme.fonts.headingFont || textFont !== theme.fonts.textFont)
      input.fonts = { headingFont, textFont };
    if (showLogoInPlayer !== theme.showLogoInPlayer) input.showLogoInPlayer = showLogoInPlayer;

    const originalColors = theme.appearanceColors || [];

    const changedColorSets: ColorSet[] = (appearanceColors || []).filter((currentSet) => {
      const previous = originalColors.find((set) => set.theme === currentSet.theme);
      if (!previous) return true;
      return !areColorSetsEqual(previous as ColorSet, currentSet);
    });

    if (changedColorSets.length > 0) input.appearanceColors = changedColorSets;

    updateThemeMutation({ variables: { input } });
  };

  useEffect(() => {
    if (!preview?.themeId) return;

    const overrides: NonNullable<ThemePreviewState['overrides']> = {
      mode: themeMode,
      appearanceColors,
      fonts: {
        headingFont,
        textFont,
      },
      name,
      showLogoInPlayer,
    };

    themePreviewVar({ themeId: preview.themeId, overrides });
  }, [appearanceColors, headingFont, textFont, themeMode, name, showLogoInPlayer, preview?.themeId]);

  useEffect(() => {
    const customFonts = data?.lsproSettings?.appearance?.supportedFonts?.filter((font) => !font.isStandard) || [];
    const headingFontDetails = customFonts?.find((font) => font.id === headingFont);
    const textFontDetails = customFonts?.find((font) => font.id === textFont);
    let style = document.getElementById('custom-font-styles') as HTMLStyleElement;

    if (!style) {
      style = document.createElement('style');
      style.id = 'custom-font-styles';
      document.head.appendChild(style);
    }

    style.textContent = '';

    if (headingFontDetails) {
      style.textContent += `
      @font-face {
        font-family: '${headingFontDetails.id}';
        src: url('${headingFontDetails.asset?.downloadLink}');
        font-display: swap;
      }
    `;
    }

    if (textFontDetails) {
      style.textContent += `
      @font-face {
        font-family: '${textFontDetails.id}';
        src: url('${textFontDetails.asset?.downloadLink}');
        font-display: swap;
      }
    `;
    }
  }, [data?.lsproSettings?.appearance?.supportedFonts, headingFont, textFont]);

  if (!theme) return null;

  return (
    <div className={styles.themeSettings}>
      <InputDescription inputId="theme-name-input" label={t('manager.branding.themeSettings.themeNameLabel')}>
        <TextInput
          data-testid="theme-name-input"
          placeholder={t('manager.branding.themeSettings.themeNamePlaceholder')}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
      </InputDescription>

      <Accordion
        data-testid="branding-accordion-logo-and-favicon"
        buttonLabel={t('manager.branding.logoAndFavicon.title')}
        buttonContent={
          <>
            <ImageIcon />
            {t('manager.branding.logoAndFavicon.title')}
          </>
        }
      >
        <LogoAndFavicon
          themeId={theme.id}
          assets={theme.assets}
          showLogoInPlayer={showLogoInPlayer}
          onShowLogoInPlayerChange={setShowLogoInPlayer}
          onActionStart={() => setAssetUploading(true)}
          onActionComplete={() => setAssetUploading(false)}
        />
      </Accordion>

      <Accordion
        data-testid="branding-accordion-theme-and-color"
        buttonLabel={t('manager.branding.themeAndColor.title')}
        buttonContent={
          <>
            <ThemeIcon />
            {t('manager.branding.themeAndColor.title')}
          </>
        }
      >
        <ThemeAndColor
          theme={themeMode}
          appearanceColors={appearanceColors}
          errors={colorErrors}
          onColorChange={handleColorChange}
          onInputBlur={validateColorValue}
          onThemeChange={setThemeMode}
        />
      </Accordion>

      <Accordion
        data-testid="branding-accordion-font-and-typography"
        buttonLabel={t('manager.branding.fontAndTypography.title')}
        buttonContent={
          <>
            <TypographyIcon />
            {t('manager.branding.fontAndTypography.title')}
          </>
        }
      >
        <FontAndTypography
          headingFont={headingFont}
          textFont={textFont}
          onHeadingFontChange={setHeadingFont}
          onTextFontChange={setTextFont}
        />
      </Accordion>

      <div className={styles.saveButtonWrapper}>
        <SaveButton
          data-testid="theme-settings-save-button"
          saving={loading || assetUploading}
          disabled={hasErrors}
          onClick={handleSave}
        />
      </div>
    </div>
  );
}
